import { useEffect } from 'react';
import { Link } from 'wouter';
import type { SeoPage } from '@/content/seo-pages';
import { applyPageMetadata } from '@/lib/page-metadata';

function usePageMetadata(page: SeoPage) {
  useEffect(() => {
    applyPageMetadata(page);
  }, [page]);
}

function kindLabel(kind: SeoPage['kind']) {
  if (kind === 'industry') return 'Industry guide';
  if (kind === 'guide') return 'Buying guide';
  return 'Case study';
}

export default function ContentPage({ page }: { page: SeoPage }) {
  usePageMetadata(page);

  return (
    <div className="min-h-[100svh] bg-background text-foreground flex flex-col font-sans selection:bg-accent selection:text-white">
      <header className="border-b border-border bg-header text-header-foreground">
        <nav aria-label="Primary navigation" className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-6">
          <Link href="/" className="min-h-11 flex items-center font-display text-xl sm:text-2xl font-bold tracking-tight text-header-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
            ATLAS AG
          </Link>
          <div className="flex items-center gap-4 sm:gap-8">
            <a href="/#work" className="hidden sm:flex min-h-11 items-center font-mono text-xs tracking-widest uppercase text-header-foreground hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              Work
            </a>
            <a href="/#capability" className="hidden sm:flex min-h-11 items-center font-mono text-xs tracking-widest uppercase text-header-foreground hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              Capability
            </a>
            <a href="/#contact" className="min-h-11 inline-flex items-center bg-accent px-4 sm:px-6 font-mono text-[10px] sm:text-xs tracking-widest uppercase text-white hover:bg-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              Start a build
            </a>
          </div>
        </nav>
      </header>

      <main className="flex-1">
        <section className="relative overflow-hidden border-b-[4px] border-accent bg-hero text-hero-foreground">
          <div className="absolute inset-0 bg-grid-pattern pointer-events-none" />
          <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36">
            <div className="max-w-5xl">
              <div className="font-mono text-xs tracking-widest text-hero-muted uppercase mb-8 flex items-center gap-4">
                <span className="w-8 h-[1px] bg-accent" />
                {page.eyebrow}
              </div>
              <p className="font-mono text-xs tracking-widest text-hero-muted uppercase mb-6">
                {kindLabel(page.kind)}
              </p>
              <h1 className="font-display text-[clamp(3rem,10vw,7.5rem)] tracking-[-0.03em] leading-[0.87] uppercase max-w-5xl">
                {page.h1}
              </h1>
              <p className="mt-10 max-w-3xl text-xl sm:text-2xl leading-relaxed text-hero-muted">
                {page.intro}
              </p>
              {page.directAnswer && (
                <div className="mt-10 max-w-4xl border-l-4 border-accent pl-6 sm:pl-8">
                  <div className="font-mono text-xs tracking-widest text-accent uppercase mb-3">Direct answer</div>
                  <p className="text-lg sm:text-xl leading-relaxed text-hero-foreground">{page.directAnswer}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-background">
          <div className="w-full max-w-[1000px] mx-auto">
            {page.sections.map((section, index) => (
              <article key={section.heading} className="border-t border-border py-12 sm:py-16 first:pt-0 first:border-t-0">
                <div className="grid md:grid-cols-[220px_minmax(0,1fr)] gap-8 sm:gap-12">
                  <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase">
                    <span className="text-accent mr-3">{String(index + 1).padStart(2, '0')}</span>
                    {kindLabel(page.kind)}
                  </div>
                  <div>
                    <h2 className="font-display text-4xl sm:text-6xl text-primary tracking-tighter leading-[0.9] uppercase mb-8">
                      {section.heading}
                    </h2>
                    <div className="space-y-6 text-[17px] sm:text-lg leading-[1.75] text-muted-foreground">
                      {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                    </div>
                    {section.bullets && (
                      <ul className="mt-8 space-y-4 border-l-2 border-accent pl-6 text-[16px] sm:text-[17px] leading-[1.65] text-primary">
                        {section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}
                      </ul>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-card border-y border-border">
          <div className="w-full max-w-[1000px] mx-auto">
            <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-6 flex items-center gap-4">
              <span className="w-8 h-[1px] bg-accent" />
              Continue
            </div>
            <h2 className="font-display text-5xl sm:text-7xl tracking-tighter leading-[0.85] uppercase mb-10">
              Build around the work.
            </h2>
            <div className="grid sm:grid-cols-2 gap-px bg-border border border-border">
              {page.relatedLinks.map((link) => {
                const external = link.href.startsWith('http');
                const className = "group min-h-20 flex items-center justify-between gap-4 bg-background px-5 sm:px-8 py-5 text-primary hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent";
                return external ? (
                  <a key={link.href} href={link.href} target="_blank" rel="noopener noreferrer" className={className}>
                    <span className="font-mono text-xs sm:text-sm tracking-wide">{link.label}</span>
                    <span aria-hidden="true" className="text-accent text-xl group-hover:translate-x-1 transition-transform">↗</span>
                  </a>
                ) : (
                  <Link key={link.href} href={link.href} className={className}>
                    <span className="font-mono text-xs sm:text-sm tracking-wide">{link.label}</span>
                    <span aria-hidden="true" className="text-accent text-xl group-hover:translate-x-1 transition-transform">→</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 bg-background">
          <div className="w-full max-w-[1000px] mx-auto flex flex-col sm:flex-row sm:items-end justify-between gap-10">
            <div className="max-w-2xl">
              <div className="font-mono text-xs tracking-widest text-muted-foreground uppercase mb-6 flex items-center gap-4">
                <span className="w-8 h-[1px] bg-accent" />
                Next step
              </div>
              <h2 className="font-display text-5xl sm:text-7xl tracking-tighter leading-[0.85] uppercase mb-6">
                Tell us what needs to exist.
              </h2>
              <p className="text-[17px] sm:text-lg leading-[1.65] text-muted-foreground">
                If you already know what you want built, describe it. If you only know what is broken, that works too.
              </p>
            </div>
            <a href="/#contact" className="shrink-0 inline-flex min-h-14 items-center justify-center bg-accent px-8 text-white font-mono text-xs tracking-widest uppercase hover:bg-accent/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
              Start a build
            </a>
          </div>
        </section>
      </main>

      <footer className="border-t border-border py-8 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="w-full max-w-[1400px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link href="/" className="font-display text-2xl tracking-tight font-bold text-primary">
            ATLAS AG
          </Link>
          <div className="flex items-center gap-6 font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
            <a href="/#contact" className="min-h-11 flex items-center hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent">Start a build</a>
            <span>&copy; {new Date().getFullYear()} Atlas AG.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}